// Select options for classification and control markings.
// This re-exports the single source of truth in
// components/prosemirror-editor/helpers/classificationLevels.js so colors and
// levels stay in sync everywhere (do not redefine colors/levels here).
import {CLASSIFICATION_LEVELS} from '../../../../../components/prosemirror-editor/helpers/classificationLevels';

export {CLASSIFICATION_LEVELS};

export const getClassificationLabel = (value) => {
    const level = CLASSIFICATION_LEVELS.find(l => l.value === value);
    return level ? level.label : 'Unknown';
}

export const getClassificationByValue = (value) => {
    if (!value) return CLASSIFICATION_LEVELS[0];
    const normalised = value.trim().toUpperCase();
    // Accept full labels like "UNCLASSIFIED" → 'U', "CONFIDENTIAL" → 'C', "SECRET" → 'S', "TOP SECRET" → 'TS'
    const aliases = {
        'UNCLASSIFIED': 'U',
        'CONFIDENTIAL': 'C',
        'SECRET': 'S',
        'TOP SECRET': 'TS',
        'TOPSECRET': 'TS',
    };
    const key = aliases[normalised] || normalised;
    return CLASSIFICATION_LEVELS.find(l => l.value === key) || CLASSIFICATION_LEVELS[0];
}

// Classification hierarchy order (U < C < S < TS) — matches dash-api's ClassificationLevel enum.
const CLASSIFICATION_ORDER = {
    'U': 0,
    'C': 1,
    'S': 2,
    'TS': 3
};

/**
 * Returns the maximum classification level between two levels
 * based on hierarchy: U < C < S < TS
 * @param {string} level1 - First classification level (U, C, S, or TS)
 * @param {string} level2 - Second classification level (U, C, S, or TS)
 * @returns {string} The higher classification level
 */
export const getMaxClassification = (level1, level2) => {
    // Default to 'U' if either level is invalid or empty
    const l1 = level1 || 'U';
    const l2 = level2 || 'U';
    
    const order1 = CLASSIFICATION_ORDER[l1] ?? 0;
    const order2 = CLASSIFICATION_ORDER[l2] ?? 0;
    
    return order1 >= order2 ? l1 : l2;
}

/**
 * Computes the maximum classification level from an array of levels
 * @param {string[]} levels - Array of classification levels
 * @returns {string} The highest classification level
 */
export const computeMaxClassification = (levels) => {
    if (!levels || levels.length === 0) return 'U';
    return levels.reduce((max, current) => getMaxClassification(max, current), 'U');
}


export const CONTROL_MARKINGS = [
    {value: '', label: 'None'},
    {value: 'FOUO', label: 'FOUO - For Official Use Only'},
    {value: 'CUI', label: 'CUI - Controlled Unclassified Information'},
    {value: 'LES', label: 'LES - Law Enforcement Sensitive'},
    {value: 'SBU', label: 'SBU - Sensitive But Unclassified'},
    {value: 'NOFORN', label: 'NOFORN - Not Releasable to Foreign Nationals'},
    {value: 'ORCON', label: 'ORCON - Originator Controlled'},
];

// Confidence levels for reports
export const CONFIDENCE_OPTIONS = [
    {value: 'low', label: 'Low'},
    {value: 'moderate', label: 'Moderate'},
    {value: 'high', label: 'High'},
];

// Production Center list (Code, Center, Center Name)
export const PRODUCTION_CENTERS = [
    {value: 'D1', center: 'DIA', label: 'DIA — Defense Intelligence Agency'},
    {value: 'EX', center: 'JAC/JIOCEUR AC', label: 'JAC/JIOCEUR AC — EUCOM JIOCEUR Analytic Center'},
    {value: 'NM', center: 'MCIA', label: 'MCIA — Marine Corps Intelligence'},
    {value: 'AF', center: 'NASIC', label: 'NASIC — National Air and Space Intelligence Center'},
    {value: 'VT', center: 'NGA', label: 'NGA — National Geospatial-Intelligence Agency'},
    {value: 'RB', center: 'NGIC', label: 'NGIC — National Ground Intelligence Center'},
    {value: 'NS', center: 'NSA', label: 'NSA — National Security Agency'},
    {value: 'NX', center: 'ONI', label: 'ONI — Office of Naval Intelligence'},
    {value: 'A1', center: 'USAFRICOM JIOC', label: 'USAFRICOM JIOC — USAFRICOM Joint Intelligence Operations Center'},
    {value: 'RT', center: 'USCENTCOM JIOC', label: 'USCENTCOM JIOC — USCENTCOM Joint Intelligence Operations Center'},
    {
        value: 'C1',
        center: 'USCYBERCOM ICC/JOC',
        label: 'USCYBERCOM ICC/JOC — USCYBERCOM Integrated Cyber Center / Joint Operations Center'
    },
    {
        value: 'EC',
        center: 'USINDOPACOM JIOC',
        label: 'USINDOPACOM JIOC — USINDOPACOM Joint Intelligence Operations Center'
    },
    {
        value: 'N1',
        center: 'USNORTHCOM JIOC',
        label: 'USNORTHCOM JIOC — USNORTHCOM Joint Intelligence Operations Center'
    },
    {
        value: 'HA',
        center: 'USSOUTHCOM JIOC',
        label: 'USSOUTHCOM JIOC — USSOUTHCOM Joint Intelligence Operations Center'
    },
    {
        value: 'S1',
        center: 'USSPACECOM JIOC',
        label: 'USSPACECOM JIOC — USSPACECOM Joint Intelligence Operations Center'
    },
    {
        value: 'YG',
        center: 'USTRANSCOM J2 (JICTRANS)',
        label: 'USTRANSCOM J2 (JICTRANS) — USTRANSCOM Intelligence Directorate'
    },
];

// Intelligence Functional Codes (IFC) - trimmed entries from "IFC Master List.csv"
export const INTELLIGENCE_FUNCTIONAL_CODES = [
    {
        value: 'IFC0000',
        parent: '',
        label: 'Non•Intelligence Subjects',
        description: 'Intelligence related policies, procedures, priorities, guidance, and other management topics.'
    },
    {
        value: 'IFC1000',
        parent: '',
        label: 'Ballistic Missile Forces',
        description: 'Capabilities, vulnerabilities, and system characteristics and performances of ballistic missile forces.'
    },
    {
        value: 'IFC1010',
        parent: 'IFC1000',
        label: 'Strategic-Level',
        description: 'Force issues: strategy, doctrine, plans, policies, and national-level guidance affecting ballistic missile forces.'
    },
    {
        value: 'IFC1011',
        parent: 'IFC1010',
        label: 'Strategic-Level Strategy, Doctrine, Plans, and Threat',
        description: 'Strategic-level strategies, doctrine, plans, or policies to enhance or employ ballistic missile forces.'
    },
    {
        value: 'IFC1012',
        parent: 'IFC1010',
        label: 'Strategic-Level Force Estimates and Trends',
        description: 'Long-term plans, capability enhancements, deployment and employment trends for ballistic missile forces.'
    },
    {
        value: 'IFC1013',
        parent: 'IFC1010',
        label: 'Strategic-Level Organization',
        description: 'Strategic-level decisionmaking system for planning, directing, and controlling ballistic missile forces.'
    },
    {
        value: 'IFC1014',
        parent: 'IFC1010',
        label: 'Strategic-Level Leadership/Participation in National Affairs',
        description: 'Role of ballistic missile forces leadership in national affairs and policy.'
    },
    {
        value: 'IFC1015',
        parent: 'IFC1010',
        label: 'Strategic-Level Personnel Policies and Resources',
        description: 'Recruitment, retention, training, and resource policies for ballistic missile forces personnel.'
    },
    {
        value: 'IFC1016',
        parent: 'IFC1010',
        label: 'Strategic-Level Training and Exercises',
        description: 'National exercises and training of ballistic missile force units.'
    },
    {
        value: 'IFC1030',
        parent: 'IFC1000',
        label: 'Ballistic Missile Forces Combat Systems RDT&E Characteristics',
        description: 'Technical characteristics, capabilities, limitations, and RDT&E programs for ballistic missile combat systems.'
    },
    {
        value: 'IFC1031',
        parent: 'IFC1030',
        label: 'Intercontinental Ballistic Missile',
        description: 'System factors for forces employing ICBMs.'
    },
    {
        value: 'IFC1032',
        parent: 'IFC1030',
        label: 'Submarine-Launched Ballistic Missile',
        description: 'System factors for SLBM forces.'
    },
    {
        value: 'IFC1040',
        parent: 'IFC1000',
        label: 'Combat Support Force Capabilities, Doctrine, and Structure',
        description: 'Capabilities and vulnerabilities of combat support units for ballistic missile forces.'
    },
    {
        value: 'IFC1050',
        parent: 'IFC1000',
        label: 'Combat Support Systems RDT&E Characteristics',
        description: 'Technical characteristics and vulnerabilities of combat support systems for ballistic missile forces.'
    },
    {
        value: 'IFC1060',
        parent: 'IFC1000',
        label: 'Unit-Level C4I Systems and Logistics',
        description: 'Deployed force C4I systems capabilities, doctrine, and logistics.'
    },
    {
        value: 'IFC1100',
        parent: '',
        label: 'Ground Forces',
        description: 'Capabilities, vulnerabilities, and system characteristics and performances of ground forces.'
    },
    {
        value: 'IFC1115',
        parent: 'IFC1100',
        label: 'Strategic-Level C4I',
        description: 'Organization, procedures, facilities, and mobile units used by army or army corps to command and control operational ground forces.'
    },
    {
        value: 'IFC1140',
        parent: 'IFC1100',
        label: 'Ground Forces Combat Systems Characteristics and Performance',
        description: 'Characteristics, capabilities, limitations, effectiveness, and vulnerabilities of ground combat systems.'
    },
    {
        value: 'IFC1150',
        parent: 'IFC1100',
        label: 'Tactical-Level Combat Support and Combat Service Support',
        description: 'Tactical-level force factors for combat support and service support units.'
    },
    {
        value: 'IFC1160',
        parent: 'IFC1100',
        label: 'Tactical-Level Combat Support and Combat Service Support Systems',
        description: 'Technical characteristics and vulnerabilities of ground forces combat service support systems.'
    },
    {
        value: 'IFC1200',
        parent: '',
        label: 'Naval Forces',
        description: 'Capabilities, vulnerabilities, and system characteristics and performances of naval forces.'
    },
    {
        value: 'IFC1210',
        parent: 'IFC1200',
        label: 'Strategic-Level Force Issues',
        description: 'Strategy, doctrine, plans, and guidance for naval forces.'
    },
    {
        value: 'IFC1220',
        parent: 'IFC1210',
        label: 'Naval Tactics',
        description: 'Force planning and employment and specific warfare discipline tactical execution.'
    },
    {
        value: 'IFC1236',
        parent: 'IFC1230',
        label: 'At-Sea Logistics/Maintenance/Repair',
        description: 'At-sea logistics, maintenance, and repair factors.'
    },
    {
        value: 'IFC1250',
        parent: 'IFC1240',
        label: 'Platform Characteristics and Performance',
        description: 'Technical description of platform structures, weapons and sensors fit, propulsion systems, and performance.'
    },
    {
        value: 'IFC1260',
        parent: 'IFC1250',
        label: 'Naval Sensors/Electronics',
        description: 'Development and operational parameters of naval sensors and electronics.'
    },
    {
        value: 'IFC1300',
        parent: '',
        label: 'Air Forces',
        description: 'Capabilities, vulnerabilities, and system characteristics and performance of air forces.'
    },
    {
        value: 'IFC1310',
        parent: 'IFC1300',
        label: 'Strategic-Level Force Issues',
        description: 'Strategy, doctrine, plans, and other directives for air forces.'
    },
    {
        value: 'IFC1330',
        parent: 'IFC1320',
        label: 'Unit-Level Force Capabilities, Doctrine, and Structure',
        description: 'Tactical-level force capabilities and vulnerabilities for air forces.'
    },
    {
        value: 'IFC1400',
        parent: '',
        label: 'Space Forces',
        description: 'Capabilities, vulnerabilities, and system characteristics and performance of space forces.'
    },
    {
        value: 'IFC1411',
        parent: 'IFC1410',
        label: 'Strategic-Level Strategy, Doctrine, Plans, and Threat',
        description: 'Strategic-level strategies, doctrine, plans, or policies to enhance space forces capabilities.'
    },
    {
        value: 'IFC1430',
        parent: 'IFC1420',
        label: 'Space Forces Structure, Capabilities, and Employment',
        description: 'Organizational structure, capabilities, and space system capabilities and performance.'
    },
    {
        value: 'IFC1500',
        parent: '',
        label: 'National Issues',
        description: 'National politics, foreign relations, strategy, and policy impacting civil and military activities.'
    },
    {
        value: 'IFC1510',
        parent: 'IFC1500',
        label: 'National Security Policies, Programs, Objectives, and Decisionmaking',
        description: 'National-level aspects of foreign military forces and national security decisionmaking.'
    },
    {
        value: 'IFC1520',
        parent: 'IFC1500',
        label: 'Support to Command and Control Warfare',
        description: 'C4I support to national command authorities including doctrine, systems, and facilities.'
    },
    {
        value: 'IFC1521',
        parent: 'IFC1520',
        label: 'National Command, Control, Communications, Computers, and Intelligence (C4I)',
        description: 'National decisionmaking structure, procedures, and facilities used by national command authorities.'
    },
    {
        value: 'IFC1530',
        parent: 'IFC1520',
        label: 'Sociological Issues and Factors',
        description: 'Social factors that impact political stability and military capabilities.'
    },
    {
        value: 'IFC1540',
        parent: 'IFC1500',
        label: 'Multiservice/Multinational Forces',
        description: 'Joint and multinational force organization, doctrine, and operations.'
    },
    {
        value: 'IFC1560',
        parent: 'IFC1550',
        label: 'Proliferation of Technology and Weapons Transfer',
        description: 'Worldwide spread and transfer of military-related technologies and weapon systems.'
    },
    {
        value: 'IFC1600',
        parent: '',
        label: 'Nuclear, Biological, and Chemical Warfare',
        description: 'Strategy, doctrine, programs, RDT&E, production, stockpile, and defenses for NBC weapons.'
    },
    {
        value: 'IFC1610',
        parent: 'IFC1600',
        label: 'Nuclear Programs',
        description: 'Characteristics and capabilities of foreign nuclear programs.'
    },
    {
        value: 'IFC1650',
        parent: 'IFC1600',
        label: 'Chemical and Biological Warfare Programs',
        description: 'Chemical and biological weapons development, acquisition, and effects.'
    },
    {
        value: 'IFC1700',
        parent: '',
        label: 'Electronics',
        description: 'Microelectronics, lasers, electro-optics, infrared, computer, sensor, and electronic warfare technologies.'
    },
    {
        value: 'IFC1710',
        parent: 'IFC1700',
        label: 'Microelectronics',
        description: 'Microelectronics research, devices, and manufacturing technology.'
    },
    {
        value: 'IFC1740',
        parent: 'IFC1700',
        label: 'Sensor Technologies',
        description: 'Sensor systems and technologies for reconnaissance, surveillance, and targeting.'
    },
    {
        value: 'IFC1810',
        parent: 'IFC1800',
        label: 'Medical Sciences',
        description: 'Medical capabilities, facilities, and RDT&E to support armed forces.'
    },
    {
        value: 'IFC1820',
        parent: 'IFC1800',
        label: 'Mathematical and Physical Sciences / Control Theory',
        description: 'Physical and environmental sciences, control theory, and operations research.'
    },
    {
        value: 'IFC1850',
        parent: 'IFC1800',
        label: 'Energy Conversion and Power Technology',
        description: 'R&D in power conversion, storage, conditioning, and tactical power generation.'
    },
    {
        value: 'IFC1900',
        parent: '',
        label: 'Resources and Economics',
        description: 'A country’s resources, industrial capabilities, and economic capacity to project power.'
    },
    {
        value: 'IFC1910',
        parent: 'IFC1900',
        label: 'Economics',
        description: 'Economic and industrial capabilities supporting development and power projection.'
    },
    {
        value: 'IFC1925',
        parent: 'IFC1920',
        label: 'Military Materiel Identification',
        description: 'Weapon producers, types, output, timeframes, and defense industrial infrastructure.'
    },
    {
        value: 'IFC2000',
        parent: '',
        label: 'Transportation',
        description: 'Capabilities and vulnerabilities of highways, railways, ports, civil aviation, and logistics.'
    },
    {
        value: 'IFC2100',
        parent: '',
        label: 'Physical Environment',
        description: 'Terrain, oceanography, coasts, meteorology, urban areas, and cultural geography.'
    },
    {
        value: 'IFC2200',
        parent: '',
        label: 'Illicit Activities and Law Enforcement',
        description: 'Organized crime, industrial espionage, and law enforcement capabilities.'
    },
    {
        value: 'IFC2300',
        parent: '',
        label: 'Intelligence and Security',
        description: 'Intelligence, counterintelligence, and security countermeasures programs and capabilities.'
    },
    {
        value: 'IFC2350',
        parent: 'IFC2300',
        label: 'Positive Intelligence Operations',
        description: 'Foreign intelligence collection programs and capabilities (HUMINT, SIGINT, IMINT, MASINT).'
    }
];
